import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import { OtcContractLayerDto } from "@/lib/api/otc";
import {
  OTC_LAYER_FIELD_REGISTRY,
  OtcLayerFieldDef,
} from "@/section/otc/contract-form-v3/fieldRegistry";

/**
 * Minimal read-only economics context shown alongside an invoice-fee form so
 * the user can see which layer they are entering a fee for.
 *
 * - Base / initial-style layers (adjustment_slot null) show the base economics
 *   row: option premium + initial premium/fee.
 * - Amendment layers show only their assigned adjustment slot's row.
 */

/** Base-layer economics fields (option-premium variant). */
const BASE_ECONOMICS_KEYS: ReadonlyArray<string> = [
  "option_premium",
  "contract_fee",
];

/** Adjustment-slot field key sets, indexed by slot number (1-based). */
const ADJ_SLOT_KEYS: ReadonlyArray<ReadonlyArray<string>> = [
  ["adj_option_premium", "adj_contract_fee", "last_adjusted_date"],
  ["adj_option_premium_two", "adj_contract_fee_two", "adjusted_date_two"],
  ["adj_option_premium_three", "adj_contract_fee_three", "adjusted_date_three"],
  ["adj_option_premium_four", "adj_contract_fee_four", "adjusted_date_four"],
  ["adj_option_premium_five", "adj_contract_fee_five", "adjusted_date_five"],
];

interface SummaryRow {
  key: string;
  label: string;
  value: string;
}

function isEmptyValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  );
}

function formatValue(def: OtcLayerFieldDef, value: unknown): string | null {
  if (isEmptyValue(value)) return null;
  if (def.widget === "date" && typeof value === "string") {
    const parsed = moment.tz(value, CommonDestinationTimezone);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return null;
}

function buildRows(
  keys: ReadonlyArray<string>,
  layer: OtcContractLayerDto,
): SummaryRow[] {
  const rows: SummaryRow[] = [];
  for (const key of keys) {
    const def = OTC_LAYER_FIELD_REGISTRY[key];
    if (!def) continue;
    const raw = layer.snapshot[key];
    const value = formatValue(def, raw);
    if (value !== null) {
      rows.push({ key, label: def.label, value });
    }
  }
  return rows;
}

export function LayerEconomicsSummary({
  layer,
}: {
  layer: OtcContractLayerDto;
}) {
  const slot = layer.adjustment_slot;
  const keys =
    typeof slot === "number" && slot >= 1 && slot <= 5
      ? ADJ_SLOT_KEYS[slot - 1]
      : BASE_ECONOMICS_KEYS;

  const rows = buildRows(keys, layer);

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
      {rows.map((row: SummaryRow) => (
        <div key={row.key} className="flex flex-col">
          <span className="text-xs text-muted-foreground">{row.label}</span>
          <span className="text-sm">{row.value}</span>
        </div>
      ))}
    </div>
  );
}
